import React, { useCallback, useState } from 'react';
import {
  Box,
  Stack,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  Divider,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Collapse,
  useDisclosure,
  Text,
  Center,
} from '@chakra-ui/react';
import Logo from '../logo';
import { Link, useNavigate } from 'react-router-dom';
import { BiChevronDown } from 'react-icons/bi';
import { useOnlyClient } from '@/hooks/use-only-client';
import { PiTShirt, PiHoodieDuotone } from 'react-icons/pi';
import { LiaMugHotSolid } from 'react-icons/lia';
import { GiMailShirt } from 'react-icons/gi';
import { GlowingText } from './glowing-text';

const root = [
  {
    path: '/',
    title: 'صفحه اصلی',
  },
  {
    title: 'محصولات',
    children: [
      // {
      //   path: '/product?tags=frame',
      //   title: 'تابلو شاسی',
      // },
      // {
      //   path: '/product?tags=poster',
      //   title: 'استیکر و پوستر',
      // },
      {
        path: '/product?tags=t-shirts',
        title: 'تیشرت',
        icon: <PiTShirt size={24} color="#2a9c84" />,
      },
      {
        path: '/product?tags=hoodies',
        title: 'هودی',
        icon: <PiHoodieDuotone size={24} color="#eec3c3" />,
      },
      {
        path: '/product?tags=dors',
        title: 'دورس',
        icon: <GiMailShirt size={24} color="#8d8d8d" />,
      },
      {
        path: '/product?tags=mug',
        title: 'ماگ',
        icon: <LiaMugHotSolid size={24} color="#319ef9" />,
      },
    ],
  },
  {
    path: '/editor/wizard',
    title: <GlowingText>شروع طراحی</GlowingText>,
  },
  // {
  //   path: '/order',
  //   title: 'پیگیری سفارش',
  // },
  //   {
  //     path: '/',
  //     title: 'همکاری در فروش',
  //   },
  {
    path: '/contact-us',
    title: 'تماس با ما',
  },
  {
    path: '/faq',
    title: 'سوالات متداول',
  },
];

function MainMenu({ items = root, submenu = true }) {
  function Inner({ items, submenu, onClose }) {
    const render = useOnlyClient();
    // eslint-disable-next-line
    if (!render) return <></>;
    const isRoot = items === root;
    return (
      <Stack
        direction={isRoot ? 'row' : 'column'}
        align={'center'}
        justify={{ base: 'space-around', sm: 'center' }}
        fontSize={{ base: '13px', md: 'sm' }}
        fontWeight={600}
        color={'blackAlpha.800'}
        spacing={isRoot ? 8 : 2}
      >
        {items.map((item, index) => {
          const showSubmenu = submenu && !!item.children?.length;
          return (
            <Popover key={index} trigger="hover" closeOnBlur onClose={onClose}>
              <PopoverTrigger>
                <Stack spacing={3} w="100%" cursor={'pointer'}>
                  {!isRoot && index !== 0 && <Divider w="100%" />}
                  <Stack
                    direction={'row'}
                    align={'center'}
                    justify={showSubmenu ? 'space-between' : 'center'}
                  >
                    <Link
                      to={item.path}
                      onClick={onClose}
                      style={{
                        userSelect: 'none',
                        pointerEvents: !item.path ? 'none' : 'all',
                        width: '100%',
                      }}
                    >
                      <Center>
                        {item.icon && <Box minW={'30px'}>{item.icon}</Box>}
                        {typeof item.title == 'string' ? (
                          <Text flex={1} whiteSpace={'nowrap'}>
                            {item.title}
                          </Text>
                        ) : (
                          item.title
                        )}
                      </Center>
                    </Link>
                    {showSubmenu && (
                      <BiChevronDown size={20} style={{ cursor: 'pointer' }} />
                    )}
                  </Stack>
                </Stack>
              </PopoverTrigger>
              {showSubmenu && (
                <PopoverContent w="fit-content" py={2} px={6}>
                  <PopoverArrow />
                  <PopoverBody>
                    <Inner
                      items={item.children}
                      submenu={submenu}
                      onClose={onClose}
                    />
                  </PopoverBody>
                </PopoverContent>
              )}
            </Popover>
          );
        })}
      </Stack>
    );
  }

  const { onClose } = useDisclosure();

  return <Inner items={items} submenu={submenu} onClose={onClose} />;
}

type SideMenuProps = {
  open: boolean;
  onClose: VoidFunction;
};
function SideMenu({ open, onClose }: SideMenuProps) {
  const close = useCallback(() => setTimeout(onClose, 300), []);
  return (
    <Drawer placement={'right'} isOpen={open} onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent maxWidth={'16rem'}>
        <DrawerCloseButton rounded={'full'} top={4} />
        <DrawerBody pt={4} px={4}>
          <Logo onClick={close} />
          <Stack fontSize={'md'} pt={8}>
            <SideMenuItem items={root} onClose={close} />
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

function SideMenuItem({ items, onClose, depth = 1, ...rest }) {
  const navigate = useNavigate();
  return (
    <Box
      {...rest}
      className="menu-item"
      sx={
        depth > 1
          ? {
              '& .menu-item': {
                px: 2,
                py: 1,
                my: 1,
                '&:nth-of-type(odd)': {
                  bg: 'gray.50',
                  borderRadius: 'md',
                },
              },
            }
          : {}
      }
    >
      {items.map((item, index) => {
        const [open, setOpen] = useState(false);
        const hasSubmenu = !!item.children?.length;
        return (
          <div key={index} className="menu-item">
            {depth === 1 && index !== 0 && <Divider my={2.5} />}
            <Stack
              direction={'row'}
              justify="space-between"
              fontSize={20 * (1 - 0.15 * depth)}
              fontWeight={500}
              onClick={() => {
                if (hasSubmenu) {
                  setOpen((p) => !p);
                } else if (item.path != null) {
                  navigate(item.path);
                  onClose();
                }
              }}
            >
              <span>{item.title}</span>
              {hasSubmenu && (
                <BiChevronDown
                  size={24}
                  style={{
                    transform: `rotate(${open ? 180 : 0}deg)`,
                    transition: 'transform 0.2s',
                    cursor: 'pointer',
                  }}
                />
              )}
            </Stack>
            {hasSubmenu && (
              <Collapse in={open}>
                <SideMenuItem
                  items={item.children}
                  onClose={onClose}
                  depth={depth + 1}
                  my={4}
                  pr={2}
                />
              </Collapse>
            )}
          </div>
        );
      })}
    </Box>
  );
}

export { MainMenu, SideMenu };
